import {reportPeriod} from './reportPeriods';
import {ONE_DAY, PERIOD_MACROS, ONE_HOUR} from './constants';
import moment from 'moment';

class SimpleDateUtils {
  // IN: a timestap (1699550194887), OUT: a number (2023)
  static getFullYearFromDate(timestamp) {
    return new Date(timestamp).getFullYear();
  }
  // IN: a timestap (1699550194887), OUT: a number from 0-11
  static getMonthFromDate(timestamp) {
    return new Date(timestamp).getMonth();
  }
  // IN: a timestap (1699550194887), OUT: a number representing the day of the month
  static getDayFromDate(timestamp) {
    return new Date(timestamp).getDate();
  }
  // OUT: the simple timestamp
  static getTimestamp(){
    return new Date().getTime();
  }
  // IN: 3 numbers representing the year/month/day  , OUT: the simple timestamp
  static getTimestampForDate(year, month, day){
    return new Date(year, month, day).getTime();
  }
  //IN: a timestamp, OUT: a string in the format 'YYYY-MM-DD'
  static getCustomDateFromTimestamp(timestamp) {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return ''; 
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0'); // luna 01–12
    const d = String(date.getDate()).padStart(2, '0');      // ziua 01–31
    return `${y}-${m}-${d}`;
  }
  //IN: a date, OUT: a string in the format 'YYYY-MM-DD'
  static getCustomDateFromDateObj(dateObj) {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  // IN: a number representing the year, a number representing the month (index, so -1), OUT: a number representing the number of the days of the input month
  static getNumberOfDaysForMonth(year, month){
    return new Date(year, month + 1, 0).getDate();
  }
  // IN: a timestamp, OUT: a timestamp, representing the last minute of the day from the IN timestamp
  static getLastMinuteOfDay(timestamp) {
    const date = new Date(timestamp);
    date.setHours(23, 59, 0, 0);
    return date.getTime();
  }
  // IN: a timestamp, OUT: a timestamp, representing the last minute of the day from the IN timestamp
  static getFirstMinuteOfDay(timestamp) {
    const date = new Date(timestamp);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }
  // IN: a timestamp, OUT: a timestamp, representing the fist day/minute of the month
  static getFirstDayOfCurrentMonth(timestamp){
    return new Date(this.getFullYearFromDate(timestamp), this.getMonthFromDate(timestamp), 1).getTime();
  }
  // IN: a moment.js object, OUT: a string with the format 'YYYY-MM-DD'
  static getDateFromMoment(value){
    return value.format("YYYY-MM-DD");
  }
  // IN: a date in format 2025-10-21, OUT: a string with the format 'Oct 21'
  static getShoterFriendlyDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  // IN: a date in format 2025-10-21, OUT: a string with the format 2025-10-14
  static getShiftedDate(dateStr, macro, interval) {
    const date = new Date(dateStr);
    switch (macro) {
      case PERIOD_MACROS.LAST_7_DAYS:
        date.setDate(date.getDate() + 7);
        break;

      case PERIOD_MACROS.CURRENT_MONTH:
        date.setMonth(date.getMonth() + 1);
        break;

      case PERIOD_MACROS.LAST_MONTH:
        date.setMonth(date.getMonth() + 1);
        break;
      default:
        date.setDate(date.getDate() + interval);
        break;
    }
    // Return date formatted as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  }
}

//Methods that will all return [minTimestamp, maxTimestamp] format
class PeriodDateUtils extends SimpleDateUtils {
  static getYesterday() {
    const currentTime = this.getTimestamp();
    const minLimitDate = this.getFirstMinuteOfDay(currentTime - ONE_DAY);
    const maxLimitDate = this.getLastMinuteOfDay(minLimitDate);
    return [minLimitDate, maxLimitDate]
  }
  static getLast7Days() {
    const yesterday = this.getYesterday();
    const minDate = new Date(yesterday[0]);
    minDate.setDate(minDate.getDate() - 6);
    minDate.setHours(0, 0, 0, 0);
    return [minDate.getTime(), yesterday[1]];
  }
  static getThisMonth() {
    const yesterday = this.getYesterday();
    const yesterdayTimestamp = this.getTimestamp() - ONE_DAY;
    const minLimitDate = this.getFirstDayOfCurrentMonth(yesterdayTimestamp);
    return [minLimitDate, yesterday[1]];
  }
  static getLastFullMonth() {
    const currentTime = this.getTimestamp();
    const thisMonth = this.getMonthFromDate(currentTime)
    const thisYear = this.getFullYearFromDate(currentTime);
    let lastMonth, lastMonthYear;
    if(thisMonth) {
      // if it's not January
      lastMonth = thisMonth - 1;
      lastMonthYear = thisYear;
    } else {
      lastMonth = 11;
      lastMonthYear = thisYear - 1;
    }
    const minLimitDate = this.getTimestampForDate(lastMonthYear, lastMonth, 1);
    const lastMonthNumberOfDays = this.getNumberOfDaysForMonth(lastMonthYear, lastMonth);
    const maxLimitDate =  this.getLastMinuteOfDay(this.getTimestampForDate(lastMonthYear, lastMonth, lastMonthNumberOfDays));
    return [minLimitDate, maxLimitDate];
  }
  // IN: array of two timestamps, OUT: an array with strings, representing all the dates in the interval of the two IN timestamps
  static getAllDates(limits) {
    const startDateString = this.getCustomDateFromTimestamp(limits[0]);
    const endDateString = this.getCustomDateFromTimestamp(limits[1]);
    const startDateObj = new Date(startDateString);
    const endDateObj = new Date(endDateString);
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      return [];
    }
    if (startDateObj > endDateObj) {
      return [];
    }
    const dates = [];
    while (startDateObj.getTime() <= endDateObj.getTime() + 3 * ONE_HOUR) {
      dates.push(this.getCustomDateFromDateObj(startDateObj));
      startDateObj.setDate(startDateObj.getDate() + 1);
    }
    return dates;
  }
  static getLast30Days() {
    const days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      days.push(d.toISOString().split("T")[0]);
    }
    return days;
  }
}

class MomentDateUtils extends PeriodDateUtils {
  static macroDates(period) {
    return reportPeriod(period, this.getTimestamp()).map(day => moment(new Date(day).toISOString().slice(0, 10), 'YYYY-MM-DD'));
  }
  static getYesterdayDates() { return this.macroDates('yesterday'); }
  static getLast7DaysDates() { return this.macroDates(PERIOD_MACROS.LAST_7_DAYS); }
  static getThisMonthDates() { return this.macroDates(PERIOD_MACROS.CURRENT_MONTH); }
  static getLastFullMonthDates() { return this.macroDates(PERIOD_MACROS.LAST_MONTH); }
}

const reportDays = period => {
  const [start, end] = reportPeriod(period, SimpleDateUtils.getTimestamp());
  const days = [];
  for (let day = start; day <= end; day += ONE_DAY) days.push(new Date(day).toISOString().slice(0, 10));
  return days;
};
const getDatesByMacro = {
  [PERIOD_MACROS.LAST_7_DAYS]: () => reportDays(PERIOD_MACROS.LAST_7_DAYS),
  [PERIOD_MACROS.LAST_MONTH]: () => reportDays(PERIOD_MACROS.LAST_MONTH),
  [PERIOD_MACROS.CURRENT_MONTH]: () => reportDays(PERIOD_MACROS.CURRENT_MONTH),
  customRange: reportDays
};

export {
    SimpleDateUtils,
    PeriodDateUtils,
    MomentDateUtils,
    getDatesByMacro
}